// src/config/menuDeepLinks.js

/**
 * Pathnames that render the homepage but land the visitor on the menu section
 * with a specific category pre-selected — short URLs for print, QR codes, and
 * ads (#514).
 *
 * This is a closed allowlist rather than an open `/menu/:categorySlug` param on
 * purpose. An open param would return HTTP 200 with the default menu for every
 * slug a crawler or a user invents, which is soft-404 content at unbounded
 * URLs. Anything not listed here still falls through to NotFound. The same
 * reasoning already governs the admin `?tab=` handling in pages/Admin.jsx.
 *
 * Each entry carries two ways to find its category, tried in order:
 *
 *   categoryId   The Clover id. Exact, and immune to the menu being renamed or
 *                translated. Optional, and only a hint — if the id is gone from
 *                the live menu (category deleted or recreated in Clover) the
 *                slug below takes over.
 *   categorySlug Matched against category *display names* via
 *                components/menu/categoryDeepLink.js. This is what self-heals
 *                when Clover reissues an id, so keep it even when the id is
 *                known.
 *
 * Both are needed because neither alone is reliable: Clover categories have no
 * slug field, and their display names are machine-translated per language by
 * netlify/functions/translate-menu.js — the Spanish menu currently renders
 * "Liquor" as "Espíritu", and a re-translation can change that at any time.
 */
export const MENU_DEEP_LINKS = {
  '/liquor': {
    path: '/liquor',
    categoryId: 'Y7HQJVDX2AWT4',
    categorySlug: 'liquor',
  },
};

/**
 * Look up the deep link for a pathname, tolerating a trailing slash and casing
 * so `/Liquor/` and `/liquor` are the same entry point — printed and
 * hand-typed URLs arrive in every shape.
 *
 * @param {string} pathname
 * @returns {{path: string, categoryId?: string, categorySlug: string}|null}
 */
export const getMenuDeepLink = (pathname = '') => {
  const normalized = String(pathname ?? '')
    .toLowerCase()
    .replace(/\/+$/, '');

  return MENU_DEEP_LINKS[normalized || '/'] || null;
};
